import { useQuery } from '@tanstack/react-query';

import { ResolvedTopicStrategy, Strategy } from '@/types';
import { useAskBluejApiContext } from '../api/context';
import { DEEP_REASONING_TOGGLE_NAME } from '../../thread/constants';

export function useStrategyQuery(refetch = true) {
  const { api } = useAskBluejApiContext();

  return useQuery({
    refetchOnWindowFocus: refetch,
    refetchOnMount: refetch,
    queryKey: ['strategies'],
    queryFn: async () => {
      const { strategies, topics, resolvedTopicStrategies } = await api.getStrategies();
      const strategiesList = strategies
        .filter(strategy => strategy.answerStrategyName !== 'error')
        .map(strategy => {
          const strategyTopic = topics.find(topic => topic.strategy === strategy.answerStrategyName);

          const displayName = strategyTopic ? `${strategy.answerStrategyName} (${strategyTopic.displayName})` : strategy.answerStrategyName;
          const normalizedStrategy = withDeepReasoningToggle(strategy);

          return {
            displayName,
            value: normalizedStrategy.answerStrategyName,
            ...normalizedStrategy
          }
        });
      const strategiesByName = strategiesList.reduce<Map<string, Strategy>>((acc, strategy) => {
        acc.set(strategy.answerStrategyName, strategy);
        return acc;
      }, new Map());
      // Keep the resolved map symmetric with `topics`. Backend-resolved entries take precedence,
      // but every available topic still gets a corresponding strategy entry.
      const normalizedResolvedTopicStrategies = topics.reduce<Record<string, ResolvedTopicStrategy>>((acc, topic) => {
        const resolvedStrategy = resolvedTopicStrategies?.[topic.value];
        const matchingStrategy = strategiesByName.get(resolvedStrategy?.answerStrategyName ?? topic.strategy);

        if (!resolvedStrategy && !matchingStrategy) {
          return acc;
        }

        acc[topic.value] = buildResolvedTopicStrategy({
          topic,
          resolvedStrategy,
          matchingStrategy,
        });

        return acc;
      }, {});

      return {
        strategiesList,
        topics,
        resolvedTopicStrategies: normalizedResolvedTopicStrategies
      }
    }
  })
}

function withDeepReasoningToggle<T extends { answerStrategyInputs?: Strategy['answerStrategyInputs'] }>(strategy: T): T {
  const answerStrategyInputs = strategy.answerStrategyInputs ?? {};
  const toggles = answerStrategyInputs.toggles ?? [];
  const deepReasoningToggle = toggles.find(toggle => toggle.name === DEEP_REASONING_TOGGLE_NAME);

  if (deepReasoningToggle || !answerStrategyInputs.deepReasoning) {
    return {
      ...strategy,
      answerStrategyInputs
    };
  }

  return {
    ...strategy,
    answerStrategyInputs: {
      ...answerStrategyInputs,
      toggles: [
        ...toggles,
        {
          name: DEEP_REASONING_TOGGLE_NAME,
          enabled: answerStrategyInputs.deepReasoning.enabled
        }
      ]
    }
  };
}

function buildResolvedTopicStrategy({
  topic,
  resolvedStrategy,
  matchingStrategy,
}: {
  topic: { strategy: string };
  resolvedStrategy?: ResolvedTopicStrategy;
  matchingStrategy?: Strategy;
}) {
  const fallbackStrategy = matchingStrategy
    ? {
      answerStrategyName: matchingStrategy.answerStrategyName,
      answerStrategyMeta: matchingStrategy.answerStrategyMeta,
      answerStrategyInputs: matchingStrategy.answerStrategyInputs,
      supportedFeatures: matchingStrategy.supportedFeatures,
    }
    : undefined;

  // The resolved strategy from the backend takes precedence,
  // but we want to ensure that any missing fields are filled in from the matching strategy (if it exists)
  // to maintain consistency with the strategies list. Finally, we apply the deep reasoning toggle normalization.
  return withDeepReasoningToggle({
    ...fallbackStrategy,
    ...resolvedStrategy,
    answerStrategyName: resolvedStrategy?.answerStrategyName ?? fallbackStrategy?.answerStrategyName ?? topic.strategy,
    answerStrategyInputs: resolvedStrategy?.answerStrategyInputs ?? fallbackStrategy?.answerStrategyInputs ?? {},
  });
}
